/**
 * Mirror of `@arbitrage-core/notifications-core`'s NotificationEvent
 * shape. Kept in sync manually because the frontend doesn't import
 * from the backend workspace; if the backend type evolves, update
 * here too. Common-types is the natural home long-term.
 */

/**
 * The 48 BASE event types. The implicit `entry_` prefix that used to
 * distinguish opener (BUY) from unwinder (SELL) events is GONE — the
 * backend now stamps an explicit `direction: 'entry'|'unwind'|'system'`
 * field on every event and the `type` is the base name only. Read
 * `evt.direction` (not the type prefix) to know which side of the
 * cycle an event is about — see `getNotificationDirection`.
 */
export type NotificationEventType =
  | 'rule_started'
  | 'rule_completed'
  | 'rule_paused'
  | 'rule_cancelled'
  | 'maker_placed'
  | 'maker_adjusted'
  | 'maker_cancelled'
  | 'maker_partial_fill'
  | 'partial_fill_accumulated'
  | 'maker_filled'
  | 'counter_placed'
  | 'counter_filled'
  | 'crossing_executed'
  | 'crossing_partial'
  | 'crossing_accumulated'
  | 'manual_order_placed'
  | 'manual_order_executed'
  | 'manual_order_partial_fill'
  | 'execution_complete'
  | 'error'
  | 'counter_failed'
  | 'out_of_balance'
  | 'rule_below_minimum'
  | 'dust_remaining'
  | 'market_takeover'
  | 'tna_negative'
  | 'net_contracts_digest'
  | 'emergency_stop'
  | 'emergency_resume'
  | 'recovery_filled'
  | 'recovery_restored'
  | 'counter_skipped'
  | 'max_volume_reached'
  | 'maker_yielded_to_counter'
  | 'platform_disconnect'
  | 'platform_reconnect'
  | 'book_stale'
  | 'book_fresh'
  | 'maker_quiet_cancel'
  | 'constraint_violated'
  | 'over_buy'
  | 'over_sell'
  | 'leg_imbalance'
  | 'engine_live_task_panic'
  | 'order_rejected_rate_limited'
  | 'order_rate_limit_waiting'
  | 'market_closed'
  | 'maker_cross_refused'
  | 'counter_stuck'
  // The all-clear that ends a `counter_stuck` episode: the engine's stuck
  // sweep saw the slot leave the shape it paged in (or move on to a different
  // counter order) and reported what the leg is still naked on. `info`, and
  // never rings — it is good news.
  | 'counter_unstuck'
  | 'counter_market_hedge'
  | 'counter_maker_walk_step'
  | 'entry_counter_maker_walk_step'
  | 'hedge_deferred'
  | 'mode_change'
  | 'hedge_unhedged_panic'
  | 'belief_position_drift'
  | 'pair_missing_token_ids'
  | 'unresolved_fill_naked_leg'
  // 2026-08-27 — `fillBookedOnceBySlot`. A venue-confirmed fill the engine
  // refused to attribute: no live maker/counter pointer, no per-order ledger
  // entry, no placement birth-record. It books NOTHING and places NOTHING
  // rather than guess a path and a role, so the believed position is SHORT by
  // a real fill until a human reconciles. CRITICAL — and, unlike its naked-leg
  // siblings, the rule is deliberately NOT paused, so this card is the only
  // thing carrying the reconcile.
  | 'unattributable_fill'
  // 2026-08-27 — MEASUREMENT, never flag-gated. The recovery is about to book a
  // fill into a slot our own placement record contradicts; because the guess
  // always seeds a MAKER it then hedges an already-covered leg. Report-only:
  // with the flag OFF the booking still happens. It routes mongo+realtime ONLY
  // (Slack's quota is per-workspace), so THIS FEED is the only surface the
  // rollout's baseline can be read from — which is why it needs a real label.
  | 'duplicate_fill_booking_detected'
  // 2026-07-31 — a trade the engine BOOKED (and hedged) later DIED at the
  // venue: the booked leg is gone while the irreversible hedge remains.
  // Same CRITICAL naked-leg class as `unresolved_fill_naked_leg`.
  | 'dead_trade_naked_leg'
  | 'orphaned_automation_fill'
  | 'rule_state_stale'
  | 'rule_state_recovered'
  | 'book_cross_saved'
  // All-clear for the boot-time system-config fetch: a pod that was reported as
  // running on guessed hedge config has read the real singleton.
  | 'system_config_recovered'
  // 2026-08-24 — Slack DELIVERY health. Slack answered every webhook POST with
  // `429 message_limit_exceeded` for ~8h and ~1.000 cards were silently lost
  // (a failed POST is discarded — no retry, no queue). These arrive over the
  // in-app feed precisely BECAUSE Slack is the broken transport.
  | 'slack_budget_warning'
  | 'slack_delivery_failing'
  | 'slack_delivery_recovered'
  // 2026-06-04 — terminal teardown events from the Rust engine (were being
  // dropped by the dispatcher until routing was added).
  | 'session_summary'
  | 'naked_remainder'
  | 'grace_hedge'
  | 'starting_position_imbalance'
  // 2026-07-03 — hedge-accounting events from the Rust engine.
  | 'over_hedged'
  | 'counter_credit_applied'
  // 2026-07-10 — counter-order lost-ACK reconciliation events.
  | 'counter_recovered'
  | 'counter_rearmed'
  | 'counter_unconfirmed'
  // 2026-07-22 — a stuck counter-hedge escalation declined to cross the book
  // above the rule's threshold / max-sum cap and left the remainder resting
  // as a GTC maker instead of taking.
  | 'counter_capped_resting'
  // 2026-08-25 — the counter/hedge PRICE LADDER (Rust engine, opener +
  // unwinder). With `counterHedgeLadderStepMs` set the counter no longer goes
  // straight out at the hedge mode's ceiling: it walks a timed ladder of
  // combined-sum targets (R0 = the rule's threshold → R1 = break-even →
  // R2 = the mode ceiling).
  //
  // `counter_ladder_step` — ONE successful rung transition. Routed
  //   `['mongo', 'realtime']` in notifications-core, deliberately NOT to
  //   Slack: it fires up to twice per cycle on every laddered leg and
  //   carries no decision for the operator.
  // `counter_ladder_leg_appreciated` — CRITICAL, routed
  //   `['mongo', 'realtime', 'slack']`. The leg we are still exposed on rose
  //   `>= counterHedgeLadderAdverseTicks` above the reference snapped at the
  //   maker fill, while its hedge was still incomplete. The engine keeps
  //   laddering the hedge and does NOT sell the appreciated leg — that is a
  //   MANUAL operator action, which is what makes it a page and not a row.
  | 'counter_ladder_step'
  | 'counter_ladder_leg_appreciated'
  // 2026-08-24 — el detector de ordenes rancias del motor. Los DOS van
  // `['mongo', 'realtime']` en notifications-core: ninguno pagina.
  //
  // `stale_resting_order_detected` — una resting que el motor cree viva pero
  //   la venue ya no reporta. `NotificationsPage` lo lista en
  //   `LIFECYCLE_NOISE_TYPES` (oculto por default): con el cancel armado el
  //   motor ya se ocupo, y llego a 217 filas en 8 minutos el dia que salio.
  // `stale_resting_order_cancel_resolved` — el cancel que SI necesita al
  //   operador, y por eso queda en el feed principal.
  //
  // Entraron al motor y a la pagina con el PR #162 sin llegar a esta union,
  // que es un `Record` exhaustivo para `NOTIFICATION_DISPLAY` — asi que
  // `tsc` quedo rojo en `main`, y como el build es `tsc && vite build`, el
  // deploy entero quedo frenado.
  | 'stale_resting_order_detected'
  | 'stale_resting_order_cancel_resolved'
  // 2026-07-20 — Sniper (live-event directional sweep) engine events. Surface
  // in the existing prediction-notifications feed (see `sniper-contract.md`).
  | 'sniper_fired'
  | 'sniper_auto_no_fired'
  | 'sniper_var_reversal'
  | 'sniper_var_rebuy'
  // 2026-08-02 — LSports RETRACTED a goal we hold a position from: the result is in
  // doubt and an exit is running. Carries the retraction's own `confidence`.
  | 'sniper_goal_cancelled'
  | 'sniper_fire_blocked'
  // 2026-07-29 — the Kalshi confidence ladder's resting exit order.
  | 'sniper_ladder_sell_placed'
  | 'sniper_ladder_sell_amended'
  | 'sniper_ladder_sell_filled'
  // 2026-08-01 — every booked BUY fill delta on a sniper order. Carries the
  // cumulative `filled` + `sizeRequested`, so partial-vs-complete is DERIVED
  // (`filled < sizeRequested`) rather than being a second event type.
  | 'sniper_buy_filled'
  // 2026-08-06 — Polymarket cancel-path events from the pending-cancel reaper.
  // The reaper re-issues a cancel for every fire-and-forget Poly cancel the
  // venue still shows resting; these are its escalation ladder.
  | 'poly_cancel_unconfirmed'
  | 'poly_cancel_abandoned'
  | 'poly_pending_cancel_registry_overflow'
  // 2026-08-06 — a Polymarket cancel+replace whose cancel could NOT be
  // confirmed off-book: the replacement is deliberately withheld so two live
  // makers can never rest on the same leg. The leg is left PARKED (inert), so
  // this is operator-visible — but it is a defensive decision working as
  // designed, not a fault, hence `warning` rather than `error`.
  | 'poly_replace_withheld'
  // 2026-08-13 — the opener's forfait protection armed: a pair collapsed to a
  // 50-50 resolution regime and the engine suspended the paths whose
  // Polymarket leg would enter above the cap. The existing position is NOT
  // unwound.
  | 'forfait_protection_triggered'
  // 2026-08-18 — the PROVISIONAL half of that brake. The cycle detector tripped
  // and entry is withheld, but nothing was written and the rule was not paused:
  // one hold window later the engine re-reads the book and either arms for real
  // (`forfait_protection_triggered`) or releases and resumes
  // (`forfait_hold_released`). `forfait_hold_started` repeats ONCE, with
  // `heldMs` set, when the hold has gone its ceiling without reaching a verdict
  // — whether or not the book reads, which `bookReadable` says; the brake stays
  // on either way, and that card is what keeps it from being invisible.
  //
  // `forfait_hold_dropped` is the third outcome and NOT a shape of the released
  // one: the hold was let go because the rule stopped holding, so no verdict was
  // taken. The release asserts a finding ("no longer closing on $0.50"); this
  // asserts nothing, and a separate type keeps it off the all-clear's green.
  | 'forfait_hold_started'
  | 'forfait_hold_released'
  | 'forfait_hold_dropped'
  // 2026-08-13 — the venue-maintenance lifecycle. A scheduled Kalshi/Polymarket
  // maintenance window used to reach the operator only as the SYMPTOM: dozens
  // of `poly_cancel_unconfirmed` cards, one per resting order, every two
  // minutes, with no closing event. These three replace it with a halt, a
  // resume, and a "the status feed is unreadable so the protection is blind".
  | 'venue_maintenance_scheduled'
  | 'venue_maintenance_halt'
  | 'venue_maintenance_panic'
  | 'venue_maintenance_window_over'
  | 'venue_maintenance_resumed'
  | 'venue_maintenance_probe_blind';

/**
 * Curated allowlist of "social-style" notification types.
 *
 * These are NOT per-operation trading-lifecycle events (fills, orders,
 * amends, rule bookends) — they are account / portfolio-level alerts the
 * operator wants surfaced like a social-network notification: a sidebar
 * bell with an unread badge + dropdown, NOT a row in the dense operations
 * feed at `/predictions/notifications`.
 *
 * Membership is the single source of truth for the split:
 *   - The operations feed EXCLUDES every type in this set (REST query +
 *     realtime prepend + type-filter dropdown) so they "move out entirely".
 *   - The sidebar bell (`useSocialNotifications` / `NotificationBell`)
 *     shows ONLY the types in this set.
 *
 * To promote a future event type into the bell, add it here — both
 * surfaces react automatically. Starts with `tna_negative`.
 */
export const SOCIAL_NOTIFICATION_TYPES: readonly NotificationEventType[] = [
  'tna_negative',
];

const SOCIAL_NOTIFICATION_TYPE_SET: ReadonlySet<string> = new Set(
  SOCIAL_NOTIFICATION_TYPES,
);

/** True when an event type belongs on the sidebar bell, not the ops feed. */
export function isSocialNotification(
  type: NotificationEventType | string,
): boolean {
  return SOCIAL_NOTIFICATION_TYPE_SET.has(type);
}

/**
 * Three-valued ON PURPOSE, and NOT a mirror of the Rust wire enum.
 *
 * The engine emits a fourth value, `critical`, but both engine-boundary
 * consumers collapse it to `error` before anything is published
 * (`notification-stream.consumer.ts` in position-opener / position-unwinder,
 * pinned by their specs), `notifications-core`'s `Severity` is 3-valued, and
 * all eight Mongo schemas enum-forbid `critical`. So it can never reach this
 * app. Adding it here would produce a Severity filter option that silently
 * matches zero rows — a false negative in an incident-triage UI.
 */
export type NotificationSeverity = 'info' | 'warning' | 'error';
export type NotificationSource = 'automation' | 'manual' | 'system';
export type NotificationPlatform = 'kalshi' | 'polymarket';

export interface NotificationEvent<TPayload = Record<string, unknown>> {
  id: string;
  type: NotificationEventType;
  /**
   * Which side of the arbitrage cycle this event belongs to. Stamped
   * explicitly by the backend dispatcher (replaced the implicit
   * `entry_` type-name prefix). Optional only so a not-yet-migrated
   * in-flight doc still type-checks; `getNotificationDirection` falls
   * back to a system/unwind default when it's absent.
   */
  direction?: NotificationDirection;
  severity: NotificationSeverity;
  source: NotificationSource;
  occurredAt: string;
  /**
   * Per-rule monotonic sequence stamped by the backend dispatcher.
   * Present iff the event has a `ruleId`. Used as the secondary
   * sort key behind `occurredAt` so two events for the same rule
   * fired in the same JS tick on the producer (canonical case:
   * `counter_filled` → `execution_complete`) render in source order
   * regardless of WS arrival order. System / manual events have no
   * `seq` because they have no `ruleId` and cross-rule ordering is
   * not a logical invariant.
   */
  seq?: number | null;
  /**
   * Global Redis-Stream cursor — the `notifications:stream` entry id
   * (`<ms>-<seq>`). Assigned by the realtime relay when it reads the
   * event from the stream; NOT set by producers, the dispatcher, or
   * the Mongo/REST history. Present only on the live socket frame +
   * replay. The FE stores it as its replay cursor and prefers it as
   * the global total-order sort key for live rows.
   */
  streamId?: string | null;
  /**
   * Per-(rule, engine eval pass) grouping id stamped by the Rust engine —
   * which eval pass produced this burst of events for the rule. Tracing /
   * grouping metadata ONLY, NOT a delivery or ordering primitive (ordering is
   * `occurredAt`/`seq`). Absent for system/health events and non-engine
   * producers.
   */
  tickId?: string | null;
  /**
   * Order of this event WITHIN its `tickId` — strictly increasing within a
   * pass, re-based each pass. Present iff `tickId` is.
   */
  intraTickSeq?: number | null;
  /**
   * Optional trace link to the specific order this event concerns. Most
   * engine payloads already carry `payload.orderId`; this hoists it for
   * tracing. NOT a delivery or ordering primitive.
   */
  orderId?: string | null;
  ruleId?: string | null;
  pairId?: string | null;
  pairName?: string | null;
  platform?: NotificationPlatform | string | null;
  kalshiTicker?: string | null;
  polyConditionId?: string | null;
  payload: TPayload;
}

/**
 * Direction axis: which side of the arbitrage cycle a notification is
 * about. Mirrors the same enum in `notification-display.ts`. Kept in
 * the type module so filter state is fully typed without importing
 * from the components folder.
 */
export type NotificationDirection = 'entry' | 'unwind' | 'system';

/** Filter state held in URL search params + the filters bar. */
export interface NotificationsFilter {
  from?: string;
  to?: string;
  ruleId?: string;
  pairId?: string;
  types?: NotificationEventType[];
  /**
   * Types to hide from the feed (Mongo `$nin`). Used to hide high-volume /
   * low-value events (maker amends) by default without enumerating every
   * other type. ANDed with `types` on the backend.
   */
  excludeTypes?: NotificationEventType[];
  direction?: NotificationDirection | 'all';
  platform?: NotificationPlatform | 'all';
  source?: NotificationSource | 'all';
  severity?: NotificationSeverity | 'all';
}

export interface NotificationsListResponse {
  items: NotificationEvent[];
  nextCursor: string | null;
  count: number;
}

/**
 * The closed vocabulary of sweep-mode operating modes carried by a
 * `mode_change` notification's `toMode` / `fromMode`. Mirrors the Rust
 * engine's mode strings. `sweep_dual_post` is the legacy (deleted 2026-07)
 * overlay mode, kept so historical cards still render.
 */
export type SweepMode =
  | 'maker_first'
  | 'sweep_rising_leg_first'
  | 'paused'
  | 'sweep_dual_post';

/**
 * The ONLY valid values of a `mode_change` EXIT card's `reason` — the
 * Rust `SweepStopReason` closed vocabulary. Shared with the engine (the
 * engine has a compile-time test asserting the two lists agree). Anything
 * else on the wire is treated as a legacy/free-form reason by the FE.
 */
export type SweepStopReason =
  | 'sweep_stopped'
  | 'edge_gone'
  | 'budget_exhausted'
  | 'naked_remainder'
  | 'hedge_failure'
  | 'maxv_reached';

/**
 * Structured payload of a `mode_change` notification, as emitted by the
 * rising-leg-first sweep engine (Unit P2E). All keys are optional so a
 * legacy card (which carried none of them) still type-checks and the FE
 * falls back to `title`/`message`.
 *
 * Three shapes share this type, disambiguated by `toMode` / `reason`:
 *   - ENTRY   — `toMode: 'sweep_rising_leg_first'`, no stop `reason`.
 *   - REVERSAL — `reason: 'reversed'` (the rising leg changed under us).
 *   - EXIT    — `toMode: 'maker_first' | 'paused'`, `reason` a SweepStopReason.
 */
export interface ModeChangeNotificationPayload {
  // Common
  fromMode?: SweepMode | string;
  toMode?: SweepMode | string;
  reason?: SweepStopReason | 'reversed' | string;
  path?: string;
  severity?: NotificationSeverity;
  /** Backend dedup backstop: how many identical cards this one folds in. */
  suppressedCount?: number;

  // ENTRY
  risingLeg?: string;
  fallingLeg?: string;
  risingSide?: string;
  sweepDirection?: string;
  velocityTicksPer100ms?: number;
  inferred?: boolean;
  contractsSwept?: number;
  frontPrice?: number;

  // REVERSAL
  oldRisingLeg?: string;
  newRisingLeg?: string;
  ownedOldRisingVwap?: number;
  reTargetCeiling?: number;

  // EXIT summary
  durationMs?: number;
  reversals?: number;
  contractsTaken?: number;
  contractsHedged?: number;
  unhedgedRemainder?: number;
  takes?: number;
  refiresSuppressed?: number;

  // Legacy fallback text (historically formatted into title/message).
  title?: string;
  message?: string;
  detail?: string;
}

/* ───────────────────────── Triage tray ─────────────────────────────── */

/**
 * The two buckets of the triage tray. Both mean the same operational thing —
 * "a leg is un-hedged and a human has to look" — and are split only because
 * their payloads and their attribution differ: `counter_stuck` belongs to a
 * RULE, an orphaned fill belongs (at best) to a PAIR.
 */
export type TriageBucket = 'counter' | 'orphan';

/**
 * The triage vocabulary, written down exactly once. Every other module asks
 * `triageBucketOf` instead of comparing against a bare type literal, so
 * widening the tray later is a one-line change here.
 */
export const TRIAGE_TYPE_BY_BUCKET: Readonly<
  Record<TriageBucket, NotificationEventType>
> = {
  counter: 'counter_stuck',
  orphan: 'orphaned_automation_fill',
};

export const TRIAGE_NOTIFICATION_TYPES: readonly NotificationEventType[] = [
  TRIAGE_TYPE_BY_BUCKET.counter,
  TRIAGE_TYPE_BY_BUCKET.orphan,
];

const TRIAGE_BUCKET_BY_TYPE: ReadonlyMap<string, TriageBucket> = new Map(
  (Object.entries(TRIAGE_TYPE_BY_BUCKET) as Array<
    [TriageBucket, NotificationEventType]
  >).map(([bucket, type]) => [type, bucket]),
);

/** The bucket an event type belongs to, or `null` when it is not a triage type. */
export function triageBucketOf(
  type: NotificationEventType | string | null | undefined,
): TriageBucket | null {
  if (!type) return null;
  return TRIAGE_BUCKET_BY_TYPE.get(type) ?? null;
}

/**
 * `readBy` stamped by the backend auto-clear (spec §4.6): the engine provably
 * hedged the same leg at market. Rendered as an explicit tag in the read view
 * — an auto-cleared card is never invisible, it moves, and `Unread` brings it
 * back.
 */
export const TRIAGE_AUTO_CLEAR_READ_BY = 'system:counter_market_hedge';

/**
 * The OTHER `readBy` the backend auto-clear stamps: the engine reported the
 * stuck episode over with nothing left un-hedged.
 *
 * Two sentinels, not one, because they answer "why did this clear" with
 * different facts — one says the engine crossed the book to cover the leg, the
 * other says the leg stopped being stuck and came out flat (a counter maker
 * that simply filled). {@link isAutoCleared} accepts both; the row renders the
 * one that applies.
 */
export const TRIAGE_AUTO_CLEAR_UNSTUCK_READ_BY = 'system:counter_unstuck';

/** Every `readBy` that means "a machine cleared this, not a person". */
export const TRIAGE_AUTO_CLEAR_READ_BYS = [
  TRIAGE_AUTO_CLEAR_READ_BY,
  TRIAGE_AUTO_CLEAR_UNSTUCK_READ_BY,
] as const;

/** Hard cap on a single read/unread write (spec §4.5). */
export const TRIAGE_MAX_IDS_PER_WRITE = 500;

export type CounterStuckKind = 'counter_resting' | 'cycle_parked';

/**
 * What the backend auto-clear (spec §4.6) stamps on a card when a market hedge
 * covered the same leg — whether or not that hedge CLEARED the card. It is the
 * only channel through which the tray can say what the engine actually
 * covered.
 *
 * 🚨 These are BOUNDS, not measurements. `hedgedQty` comes from the venue ack,
 * which under-reports (Polymarket acks `delayed` / `unmatched` with
 * `filled_qty: 0` and fills afterwards), so it is a FLOOR on what crossed and
 * `restedQty` a CEILING on what is still open. Every surface renders them as
 * *at least X*, never as an exact figure — a card claiming more precision than
 * the datum has is the failure this tray exists to end.
 *
 * `restedQty` / `crossedPrice` / `origin` are OPTIONAL because the backend
 * omits them when the hedge did not carry them. They are never defaulted: a
 * `0` nobody sent reads as *covered nothing* / *free*.
 */
export interface CounterStuckAutoClear {
  /** FLOOR on the contracts the market hedge crossed. */
  hedgedQty: number;
  hedgeOccurredAt: string;
  /** CEILING on the tail still resting after the cross. */
  restedQty?: number;
  crossedPrice?: number;
  origin?: string;
}

/**
 * `payload.autoResolved` — the engine reported the stuck episode ENDED, and
 * this is what the leg was still naked on at that instant.
 *
 * 🚨 Its OWN field, beside `autoCleared` and never on top of it. The two
 * describe different facts — a CROSS ("we bought N at $X") and an episode
 * ending — and the backend `$set`s whole subdocuments, so sharing the slot
 * meant a resolution DELETED the hedge's `hedgedQty` and the partial-hedge
 * line disappeared off an unread card. Both can be true at once: the hedge
 * covered 45 of 50, and by the time the episode ended the other 5 were covered
 * too.
 *
 * It has no `hedgedQty` and never will — there is no cross to describe when
 * the counter maker simply fills. `nakedQty` is the residual, and unlike the
 * hedge's `restedQty` it is not a ceiling derived from an under-reporting ack:
 * it is the engine's own never-understate estimator, so it errs UP.
 */
export interface CounterStuckUnstuckClear {
  resolvedBy: 'counter_unstuck';
  resolvedAt: string;
  /** Contracts still un-hedged when the episode ended. `0` is the all-clear. */
  nakedQty?: number;
  /** `left_state` | `counter_replaced` — reported, never acted on. */
  reason?: string;
}

/** `payload` of a `counter_stuck` event (spec §3.1). */
export interface CounterStuckPayload {
  /** The COUNTER venue. */
  platform?: NotificationPlatform | string | null;
  path?: string | null;
  kind?: CounterStuckKind | string | null;
  makerPlatform?: NotificationPlatform | string | null;
  makerSide?: string | null;
  counterSide?: string | null;
  unhedgedQty?: number | null;
  restingSecs?: number | null;
  makerFillPrice?: number | null;
  /**
   * Seconds until the stuck counter escalates to a market hedge. `null` is the
   * TERMINAL case — the rule has no `counterStuckMarketHedgeSeconds`, so
   * nothing will ever escalate and this card is the last word on those
   * contracts.
   */
  marketHedgeWindowSecs?: number | null;
  orderId?: string | null;
  counterPrice?: number | null;
  /**
   * Stamped by the auto-clear on EVERY attributable hedge, before the
   * `restedQty === 0` gate — so an UNREAD card can carry one, and that is the
   * partial-coverage case the annotation exists for.
   */
  autoCleared?: CounterStuckAutoClear | null;
  /** The episode-resolution annotation — see {@link CounterStuckUnstuckClear}. */
  autoResolved?: CounterStuckUnstuckClear | null;
}

/**
 * The annotation, or `null` when there is none to read. `hedgedQty` is the one
 * field the backend always writes, so a stamp without a finite one is not an
 * annotation — and a surface must never invent it.
 */
export function autoClearOf(
  payload: CounterStuckPayload | null | undefined,
): CounterStuckAutoClear | null {
  const annotation = payload?.autoCleared;
  if (!annotation) return null;
  return typeof annotation.hedgedQty === 'number' &&
    Number.isFinite(annotation.hedgedQty)
    ? annotation
    : null;
}

/**
 * The episode-resolution annotation, or `null`.
 *
 * Reads its own field AND still checks `resolvedBy`: the field is what keeps
 * it apart from the hedge's annotation, and the tag is what keeps a future
 * writer from quietly reusing the slot for something else.
 */
export function unstuckClearOf(
  payload: CounterStuckPayload | null | undefined,
): CounterStuckUnstuckClear | null {
  const annotation = payload?.autoResolved;
  return annotation?.resolvedBy === 'counter_unstuck' ? annotation : null;
}

/**
 * True when NO market hedge will ever escalate this card. `null` and `0` both
 * mean "no window" per spec §5.2; an ABSENT field is treated the same, because
 * the alternative is rendering "escalates in Ns" without an N.
 */
export function hasNoMarketHedgeWindow(
  marketHedgeWindowSecs: number | null | undefined,
): boolean {
  return (
    marketHedgeWindowSecs === null ||
    marketHedgeWindowSecs === undefined ||
    marketHedgeWindowSecs === 0
  );
}

export type OrphanedFillReason =
  | 'rule_not_found'
  | 'unparseable_automation_coid'
  | 'engine_not_live'
  | 'verdict_lookup_error';

/** `payload` of an `orphaned_automation_fill` event (spec §3.2 + §4.2). */
export interface OrphanedAutomationFillPayload {
  platform?: NotificationPlatform | string | null;
  direction?: string | null;
  marketTitle?: string | null;
  marketUrl?: string | null;
  /** Kalshi ticker, or Polymarket tokenId / conditionId. */
  marketId?: string | null;
  orderId?: string | null;
  filledQty?: number | null;
  clientOrderId?: string | null;
  ruleIdPrefix?: string | null;
  reason?: OrphanedFillReason | string | null;
  /**
   * How the producer tied this fill to a pair. `null` means it could NOT be
   * tied to one — the row renders as `unattributed`, never as a blank that
   * reads like "no pair".
   */
  attributedBy?: 'market-identity' | null;
}

/** A notification carrying the server-side, global read state (spec §4.3). */
/**
 * The opener group the backend attributed a card to.
 *
 * `id: null` is the honest "could not be placed on exactly one group" —
 * ungrouped rule, deleted rule, orphan whose pair carries rules in two groups.
 * A `name` of `null` with a non-null `id` means the group no longer exists;
 * the row shows the id rather than inventing a name.
 */
export interface TriageCardGroup {
  id?: string | null;
  name?: string | null;
  color?: string | null;
}

/** A deep link to one leg's market page, resolved server-side from the pair. */
export interface TriageMarketLink {
  platform: string;
  url: string;
}

export interface TriageNotification<TPayload = Record<string, unknown>>
  extends NotificationEvent<TPayload> {
  /**
   * Both legs' market pages, when the pair knows them.
   *
   * An EMPTY list is a real answer, not a missing one: a Polymarket
   * `conditionId` URL 404s on click, so a pair with no slug renders no
   * Polymarket link at all rather than a broken one.
   */
  markets?: TriageMarketLink[];
  /** ISO timestamp of the clear, or `null`/absent while unread. */
  readAt?: string | null;
  /** Resolved at read time by the triage endpoint — never persisted. */
  group?: TriageCardGroup | null;
  /** Auth0 `sub` of whoever cleared it, or `TRIAGE_AUTO_CLEAR_READ_BY`. */
  readBy?: string | null;
}

export type CounterStuckNotification = TriageNotification<CounterStuckPayload>;
export type OrphanedFillNotification =
  TriageNotification<OrphanedAutomationFillPayload>;

/** True when the backend auto-clear cleared this card, not a human. */
export function isAutoCleared(evt: { readBy?: string | null }): boolean {
  return (TRIAGE_AUTO_CLEAR_READ_BYS as readonly string[]).includes(
    evt.readBy ?? '',
  );
}

/** True when the clear came from the episode-resolution path specifically. */
export function isUnstuckCleared(evt: { readBy?: string | null }): boolean {
  return evt.readBy === TRIAGE_AUTO_CLEAR_UNSTUCK_READ_BY;
}

/** One bucket of `GET /notifications/triage` (spec §4.5). */
export interface TriageBucketPage<TPayload = Record<string, unknown>> {
  /**
   * Unread count, capped server-side at 1000. When `capped` is true the real
   * number is unknown and the UI renders `999+` rather than a number that
   * stopped being true.
   */
  unread: number;
  capped?: boolean;
  /**
   * Unread cards the server's pair scope REMOVED from `unread` and `items`.
   *
   * 🚨 The scope is an allow-list, so it hides cards naming legs nobody has
   * covered. Hiding them is the operator's own setting; hiding them SILENTLY
   * is not, so this number is rendered every time it is non-zero.
   */
  hidden: number;
  /**
   * Of `hidden`, the ones carrying no `pairId` at all — an orphaned fill the
   * attribution could not name. The least classifiable naked money in the
   * system, counted apart from cards hidden because their market is over.
   */
  hiddenUnattributed: number;
  /**
   * True when `hidden` hit the server's 1000 ceiling and is a FLOOR — same
   * contract as `capped` for `unread`. Rendered as `999+`: a saturated count
   * printed as an exact one says "1000 hidden" over 3000, which is the class
   * of number this screen exists to end. `hiddenUnattributed` is a subset of
   * `hidden`, so it cannot be complete when `hidden` is not.
   */
  hiddenCapped: boolean;
  items: Array<TriageNotification<TPayload>>;
}

export interface TriageListResponse {
  counter: TriageBucketPage<CounterStuckPayload>;
  orphan: TriageBucketPage<OrphanedAutomationFillPayload>;
  /** ISO age of the oldest unread card, `null` when nothing is unread. */
  oldestUnreadAt: string | null;
  /**
   * True when the response includes cards on retired pairs.
   *
   * 🚨 The label on every other number here, and the ONLY honest input to the
   * banner that says so. Reading the URL toggle instead is wrong under
   * `keepPreviousData`: across a toggle flip react-query serves the previous
   * scope's response while the new one is in flight, so the URL and the
   * numbers on screen disagree for one round-trip — in the direction that
   * renders the widened counts under no banner at all.
   */
  includesRetired: boolean;
  /**
   * True when the server could not resolve the pair-lifecycle scope and served
   * the tray UNFILTERED. The tray says so: a count that quietly means
   * something else than it did a minute ago is the failure this screen exists
   * to end.
   */
  pairScopeCapped: boolean;
  computedAt: string;
}

/** Result of `POST /notifications/read`. */
export interface TriageReadResponse {
  matched: number;
  modified: number;
  alreadyRead: number;
  notFound: number;
}

/**
 * Result of `POST /notifications/unread` — the SAME shape except the third
 * field, which the controller returns as `alreadyUnread`. Sharing one type
 * with the read response would hand the next reader `undefined` from
 * `alreadyRead` with the compiler green.
 */
export interface TriageUnreadResponse {
  matched: number;
  modified: number;
  alreadyUnread: number;
  notFound: number;
}

/**
 * The `notification_read` socket frame (spec §4.8). It may arrive DUPLICATED:
 * the relay runs on several replicas with no consumer group. Every consumer
 * of this frame must be idempotent.
 */
export interface TriageReadFrame {
  ids: string[];
  readAt: string | null;
  readBy: string | null;
}

/** Socket event name of the frame above, on the existing `notifications` room. */
export const TRIAGE_READ_EVENT = 'notification_read';
